/**
 * Selector de tramo y de comparación para Analíticas.
 *
 * Dos decisiones que no son evidentes:
 *
 * 1. Los atajos de "últimos N días" acaban AYER. Hoy va por la mitad, y meterlo
 *    dentro hunde el total contra un periodo de días completos: la comparación
 *    diría que caes cada mañana y te recuperas cada noche. "Hoy" existe aparte,
 *    para cuando lo que quieres es justamente mirar el día en curso.
 *
 * 2. Un solo mes a la vista, y los días de los meses vecinos no se dibujan. Con
 *    dos rejillas, el 1 de agosto caía al final de julio y al principio de
 *    agosto a la vez, y la selección se pintaba en ambos: un tramo de cuatro
 *    días parecía ocho.
 *
 * 3. El calendario trabaja con cadenas `YYYY-MM-DD` y nunca con `Date` locales.
 *    Un `new Date('2026-08-04')` se interpreta en UTC y al leerlo con métodos
 *    locales puede devolver el día 3 — al oeste de Greenwich el calendario
 *    entero se corre un día. Con cadenas eso no puede pasar.
 *
 * Lo usan también las Citas, que miran hacia DELANTE: allí el futuro sí tiene
 * datos y los atajos no pueden ser los de Analíticas —el servidor de Analíticas
 * es quien los resuelve—. Para eso están `allowFuture` y `shortcuts`; sin ellos
 * el selector es exactamente el de siempre.
 */
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { AnalyticsCompare, AnalyticsQuery, AnalyticsRange } from '../../lib/api'
import { IconCalendarEvent, IconCheck, IconChevronDown, IconChevronLeft, IconChevronRight } from '@tabler/icons-react'
import { useI18n } from '../../lib/i18n'
import type { Language, Translate } from '@webbuilder/i18n'

/**
 * Los atajos, en dos grupos con su encabezado.
 *
 * La separación no es decorativa: "últimos 30 días" cuenta hacia atrás desde
 * ayer y "último mes" es el mes natural completo. Rara vez cubren las mismas
 * fechas, y quien cierra cuentas necesita el segundo. Ponerlos mezclados en una
 * sola lista invitaba a coger uno por otro.
 */
/* `labelKey` y no `label`: estas listas se construyen al cargar el módulo,
   donde todavía no hay traductor. Guardan la clave y el rótulo se resuelve al
   pintar, igual que en `WorkspaceSidebar`. */
export const RANGE_PRESETS: { value: AnalyticsRange; labelKey: string; group: 'rolling' | 'calendar' }[] = [
  { value: 'today', labelKey: 'range.today', group: 'rolling' },
  { value: 'yesterday', labelKey: 'range.yesterday', group: 'rolling' },
  { value: '7d', labelKey: 'range.last7', group: 'rolling' },
  { value: '30d', labelKey: 'range.last30', group: 'rolling' },
  { value: '90d', labelKey: 'range.last90', group: 'rolling' },
  { value: '12m', labelKey: 'range.last12m', group: 'rolling' },
  { value: 'lastWeek', labelKey: 'range.lastWeek', group: 'calendar' },
  { value: 'lastMonth', labelKey: 'range.lastMonth', group: 'calendar' },
  { value: 'lastYear', labelKey: 'range.lastYear', group: 'calendar' },
]

const PRESET_GROUPS: { id: 'rolling' | 'calendar'; labelKey: string }[] = [
  { id: 'rolling', labelKey: 'range.group.rolling' },
  { id: 'calendar', labelKey: 'range.group.calendar' },
]

/**
 * Un atajo que ya trae sus fechas.
 *
 * Los de Analíticas son un nombre (`30d`) que resuelve el servidor. Fuera de
 * Analíticas no hay servidor que los resuelva, así que quien llama los calcula
 * y el selector sólo los ofrece: pulsar uno aplica ese tramo como propio.
 */
export type RangeShortcut = { labelKey: string; from: string; to: string }

const COMPARE_OPTIONS: { value: AnalyticsCompare; labelKey: string; hintKey: string }[] = [
  { value: 'previous', labelKey: 'range.compare.previous', hintKey: 'range.compare.previousHint' },
  { value: 'year', labelKey: 'range.compare.year', hintKey: 'range.compare.yearHint' },
  { value: 'none', labelKey: 'range.compare.none', hintKey: 'range.compare.noneHint' },
]

/**
 * Los meses y las iniciales de los días, en el idioma de quien mira.
 *
 * Eran dos arrays fijos en español —incluida la «X» del miércoles, que sólo
 * significa algo en castellano—. `Intl` los da en cualquier idioma y además
 * acierta con las abreviaturas, que no son recortar a tres letras.
 *
 * El 1 de agosto de 2021 fue domingo, que es por donde empieza la rejilla.
 */
function calendarNames(language: Language): { months: string[]; weekdays: string[] } {
  const month = new Intl.DateTimeFormat(language, { month: 'long', timeZone: 'UTC' })
  const weekday = new Intl.DateTimeFormat(language, { weekday: 'narrow', timeZone: 'UTC' })
  return {
    months: Array.from({ length: 12 }, (_, m) => month.format(Date.UTC(2021, m, 1))),
    weekdays: Array.from({ length: 7 }, (_, d) => weekday.format(Date.UTC(2021, 7, 1 + d))),
  }
}

/** Hoy en la zona del proyecto, como `YYYY-MM-DD`. */
function todayIn(timezone?: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone || 'UTC', year: 'numeric', month: '2-digit', day: '2-digit',
    }).formatToParts(new Date())
    const value = (part: Intl.DateTimeFormatPartTypes) => parts.find(({ type }) => type === part)?.value
    const year = value('year')
    const month = value('month')
    const day = value('day')
    return year && month && day ? `${year}-${month}-${day}` : new Date().toISOString().slice(0, 10)
  } catch {
    return new Date().toISOString().slice(0, 10)
  }
}

function shiftDay(day: string, delta: number): string {
  const at = new Date(`${day}T00:00:00Z`)
  at.setUTCDate(at.getUTCDate() + delta)
  return at.toISOString().slice(0, 10)
}

function prettyDay(day: string, language: Language): string {
  const [y, m, d] = day.split('-').map(Number)
  if (!y || !m || !d) return day
  /* `timeZone: 'UTC'` porque el día viene como `YYYY-MM-DD` sin hora: dejarlo
     a la zona local adelanta o atrasa la fecha según dónde se mire. */
  return new Intl.DateTimeFormat(language, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
    .format(Date.UTC(y, m - 1, d))
}

/**
 * Etiqueta del botón: el atajo si lo hay, y si no las dos fechas.
 *
 * Recibe el traductor y el idioma en vez de leerlos de un hook: la llaman tres
 * pantallas y también se usa fuera de un componente, donde no hay hooks.
 */
export function rangeLabel(
  i18n: { t: Translate; language: Language },
  query: AnalyticsQuery,
  window?: { from: string; to: string },
): string {
  if (query.range !== 'custom') {
    const preset = RANGE_PRESETS.find((p) => p.value === query.range)
    return preset ? i18n.t(preset.labelKey) : i18n.t('range.period')
  }
  const from = query.from ?? window?.from
  const to = query.to ?? window?.to
  if (!from || !to) return i18n.t('range.custom')
  const day = (value: string) => prettyDay(value, i18n.language)
  return from === to ? day(from) : `${day(from)} – ${day(to)}`
}

type Cell = { day: string } | null

/**
 * Rejilla de un mes: sólo sus días, con huecos vacíos antes y después.
 *
 * Los días de los meses vecinos NO se dibujan. Con dos meses a la vista, el 1
 * de agosto cae a la vez al final de la rejilla de julio y al principio de la
 * de agosto — y al pintar la selección en los dos sitios, un tramo de cuatro
 * días parecía ocho repartidos por la pantalla. Dejando el hueco en blanco cada
 * fecha existe una sola vez y la selección se lee de un vistazo.
 */
function monthGrid(year: number, month: number): Cell[] {
  const first = new Date(Date.UTC(year, month, 1))
  // getUTCDay: domingo es 0. Restamos uno para que la semana empiece en lunes.
  /* Domingo primero, como el calendario de Reservas: dos calendarios del mismo
     panel con semanas distintas hacen contar mal al cambiar de uno a otro. */
  const lead = first.getUTCDay()
  const length = new Date(Date.UTC(year, month + 1, 0)).getUTCDate()
  const cells: Cell[] = Array.from({ length: lead }, () => null)
  for (let day = 1; day <= length; day += 1) {
    cells.push({ day: `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}` })
  }
  // Se completa la última semana y ahí se para: una fila vacía de más sólo
  // añade altura al desplegable.
  while (cells.length % 7 !== 0) cells.push(null)
  return cells
}

function MonthView({ year, month, from, to, hover, max, min = '', onPick, onHover }: {
  year: number
  month: number
  from: string
  to: string
  hover: string
  max: string
  min?: string | undefined
  onPick: (day: string) => void
  onHover: (day: string) => void
}) {
  const { language } = useI18n()
  const { months, weekdays } = calendarNames(language)
  // Mientras eliges el segundo extremo, el que sigue al ratón hace de final: sin
  // eso no se ve qué tramo estás a punto de escoger.
  const end = to || hover
  const [lo, hi] = from && end ? (from <= end ? [from, end] : [end, from]) : ['', '']

  return (
    <div className="dash-cal-month">
      <div className="dash-cal-month-name">{months[month]} {year}</div>
      <div className="dash-cal-weekdays" aria-hidden="true">
        {weekdays.map((d, i) => <span key={`${d}-${i}`}>{d}</span>)}
      </div>
      <div className="dash-cal-grid">
        {monthGrid(year, month).map((cell, index) => {
          if (!cell) return <span className="dash-cal-blank" key={`blank-${index}`} aria-hidden="true" />
          const disabled = cell.day > max || cell.day < min
          const selected = cell.day === from || cell.day === to
          const inRange = Boolean(lo) && cell.day >= lo && cell.day <= hi
          return (
            <button
              key={cell.day}
              type="button"
              disabled={disabled}
              className={[
                'dash-cal-day',
                selected ? 'is-selected' : '',
                inRange && !selected ? 'is-in-range' : '',
                cell.day === lo ? 'is-start' : '',
                cell.day === hi ? 'is-end' : '',
              ].filter(Boolean).join(' ')}
              onClick={() => onPick(cell.day)}
              onMouseEnter={() => onHover(cell.day)}
            >
              {Number(cell.day.slice(8, 10))}
            </button>
          )
        })}
      </div>
    </div>
  )
}

/**
 * El mismo calendario del selector de periodos, para una sola fecha.
 *
 * Una cita no necesita los atajos ni el segundo mes de Analíticas: necesita
 * elegir un día sin caer en el popup nativo —distinto en cada navegador—. La
 * rejilla, los meses localizados y los estados de foco siguen siendo los mismos
 * para que no aparezca otro calendario dentro del dashboard.
 */
export function SingleDatePicker({ value, timezone, disabled = false, placeholder, label, min, onChange }: {
  value: string
  timezone?: string | undefined
  disabled?: boolean
  min?: string | undefined
  /** Lo que dice sin fecha elegida: hay campos en los que vacío es un valor (la entrada del blog). */
  placeholder?: string | undefined
  /** El nombre del calendario para los lectores de pantalla; por defecto, el de una cita. */
  label?: string | undefined
  onChange: (day: string) => void
}) {
  const { t, language } = useI18n()
  const [open, setOpen] = useState(false)
  const boxRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number; above: boolean } | null>(null)
  const cursorFor = (day: string) => ({ year: Number(day.slice(0, 4)), month: Number(day.slice(5, 7)) - 1 })
  const [cursor, setCursor] = useState(() => cursorFor(value || todayIn(timezone)))

  useEffect(() => {
    if (!open) return
    setCursor(cursorFor(value || todayIn(timezone)))
    // El cursor se reinicia al abrir, no mientras se explora otro mes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      if (!boxRef.current?.contains(target) && !popoverRef.current?.contains(target)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false) }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  /* El calendario se dibuja en `document.body`, fuera del modal. Así no suma
     altura al formulario ni queda cortado por su `overflow: hidden`. La
     posición se actualiza también al desplazarse el cuerpo del formulario. */
  useEffect(() => {
    if (!open) {
      setPopoverPosition(null)
      return
    }

    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect()
      if (!rect) return
      const margin = 12
      const width = 272
      const estimatedHeight = 340
      const above = window.innerHeight - rect.bottom < estimatedHeight && rect.top > estimatedHeight
      setPopoverPosition({
        top: above ? rect.top - 6 : rect.bottom + 6,
        left: Math.max(margin, Math.min(rect.left, window.innerWidth - width - margin)),
        above,
      })
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [open])

  const step = (delta: number) => {
    const at = new Date(Date.UTC(cursor.year, cursor.month + delta, 1))
    setCursor({ year: at.getUTCFullYear(), month: at.getUTCMonth() })
  }

  return (
    <div className={`dash-single-date-picker${open ? ' is-open' : ''}`} ref={boxRef}>
      <button
        ref={triggerRef}
        type="button"
        className="dash-single-date-trigger"
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <IconCalendarEvent size={15} aria-hidden="true" />
        <span className={value ? undefined : 'is-placeholder'}>{value ? prettyDay(value, language) : placeholder}</span>
        <IconChevronDown size={14} aria-hidden="true" />
      </button>
      {open && popoverPosition && typeof document !== 'undefined' ? createPortal(
        <div
          className={`dash-single-date-popover${popoverPosition.above ? ' is-above' : ''}`}
          ref={popoverRef}
          role="dialog"
          aria-label={label ?? t('bookings.new.day')}
          style={{ top: popoverPosition.top, left: popoverPosition.left }}
        >
          <div className="dash-range-calendar">
            <div className="dash-cal-head">
              <button type="button" aria-label={t('range.prevMonth')} onClick={() => step(-1)}><IconChevronLeft size={15} /></button>
              <span>{prettyDay(value || todayIn(timezone), language)}</span>
              <button type="button" aria-label={t('range.nextMonth')} onClick={() => step(1)}><IconChevronRight size={15} /></button>
            </div>
            <div className="dash-cal-months">
              <MonthView
                {...cursor}
                from={value}
                to={value}
                hover=""
                max="9999-12-31"
                min={min}
                onHover={() => {}}
                onPick={(day) => {
                  onChange(day)
                  setOpen(false)
                }}
              />
            </div>
          </div>
        </div>,
        document.body,
      ) : null}
    </div>
  )
}

/**
 * La comparación, como control propio junto al de fecha.
 *
 * Vivía dentro del desplegable del calendario, y ahí estaba escondida: cambiar
 * contra qué se mide es tan frecuente como cambiar el tramo, y obligaba a abrir
 * el calendario —y a arriesgarse a tocar una fecha— para llegar a ella.
 */
export function CompareSelect({ query, disabled = false, onChange }: {
  query: AnalyticsQuery
  disabled?: boolean
  onChange: (next: AnalyticsQuery) => void
}) {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  const boxRef = useRef<HTMLDivElement>(null)
  const compare = query.compare ?? 'previous'
  const active = COMPARE_OPTIONS.find((option) => option.value === compare) ?? COMPARE_OPTIONS[0]

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false) }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const pick = (value: AnalyticsCompare) => {
    onChange({ ...query, compare: value })
    setOpen(false)
  }

  return (
    <div className="dash-range-picker dash-compare-picker" ref={boxRef}>
      <button
        type="button"
        className="dash-secondary-btn dash-range-trigger"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span>{active ? t(active.labelKey) : t('range.compare.trigger')}</span>
        <IconChevronDown size={14} aria-hidden="true" />
      </button>

      {open ? (
        <div className="dash-compare-popover" role="listbox" aria-label={t('range.compare.aria')}>
          {COMPARE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={compare === option.value}
              className={compare === option.value ? 'is-active' : ''}
              onClick={() => pick(option.value)}
            >
              <span className="dash-compare-option-copy">
                <strong>{t(option.labelKey)}</strong>
                {/* La explicación a la vista y no en un `title`: elegir contra
                    qué se mide cambia todas las cifras de la pantalla, y "año
                    anterior" frente a "periodo anterior" no se distingue por el
                    nombre si no sabes ya lo que significan. */}
                <span>{t(option.hintKey)}</span>
              </span>
              {compare === option.value ? <IconCheck size={14} /> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}

export function DateRangePicker({ query, window, timezone, allowFuture = false, shortcuts, onChange }: {
  query: AnalyticsQuery
  /** El tramo ya resuelto por el servidor, para arrancar el calendario ahí. */
  window?: { from: string; to: string; days: number } | undefined
  timezone?: string | undefined
  /**
   * Dejar elegir días después de hoy.
   *
   * En Analíticas no: el futuro no tiene datos, y ofrecerlo sólo produce
   * tramos vacíos. En las Citas es al revés — lo que se viene a mirar es lo que
   * todavía no ha pasado.
   */
  allowFuture?: boolean
  /**
   * Atajos propios en lugar de los de Analíticas, con sus fechas ya puestas.
   * Ver `RangeShortcut`.
   */
  shortcuts?: RangeShortcut[] | undefined
  onChange: (next: AnalyticsQuery) => void
}) {
  const i18n = useI18n()
  const { t, language } = i18n
  const [open, setOpen] = useState(false)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [hover, setHover] = useState('')
  const boxRef = useRef<HTMLDivElement>(null)

  const today = todayIn(timezone)
  // El futuro no tiene datos en Analíticas, y ofrecerlo sólo produce tramos
  // vacíos. Donde sí los tiene, quien llama lo abre.
  const max = allowFuture ? '9999-12-31' : today

  /** El mes siguiente al del cursor, para la segunda rejilla. */
  const nextMonth = (at: { year: number; month: number }) => {
    const d = new Date(Date.UTC(at.year, at.month + 1, 1))
    return { year: d.getUTCFullYear(), month: d.getUTCMonth() }
  }

  /** Dónde arranca el calendario: dos meses que enseñen el tramo puesto. */
  const cursorFor = (fromDay: string | undefined, toDay: string | undefined) => {
    /* Mirando hacia atrás se ancla en el FINAL —el tramo acaba en ese mes y se
       enseña con el anterior—. Mirando hacia delante, en el INICIO: un tramo de
       un año hacia el futuro abría en su último mes, lejísimos de hoy. */
    const anchor = allowFuture ? (fromDay ?? today) : (toDay ?? today)
    const offset = allowFuture ? 1 : 2
    const at = new Date(Date.UTC(Number(anchor.slice(0, 4)), Number(anchor.slice(5, 7)) - offset, 1))
    return { year: at.getUTCFullYear(), month: at.getUTCMonth() }
  }

  const [cursor, setCursor] = useState(() => cursorFor(query.from ?? window?.from, query.to ?? window?.to))

  /**
   * Al ABRIR, el calendario parte de lo que hay puesto. Sólo al abrir.
   *
   * Ahora que el desplegable no se cierra al elegir, este efecto seguía vivo
   * mientras seleccionabas: bastaba con que llegara una respuesta del servidor
   * para que reescribiera `from` y `to` desde la consulta y te borrara el primer
   * clic. Atarlo a la apertura y a nada más lo cierra.
   */
  useEffect(() => {
    if (!open) return
    setFrom(query.from ?? window?.from ?? '')
    setTo(query.to ?? window?.to ?? '')
    setHover('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  /**
   * Elegir un atajo con el desplegable abierto sí tiene que mover el calendario:
   * si no, seguirías viendo marcado el tramo anterior mientras la gráfica ya
   * enseña otro. Sólo aplica a los atajos — con un tramo propio manda lo que
   * estés eligiendo, que puede estar a medias.
   */
  useEffect(() => {
    if (!open || query.range === 'custom' || !window) return
    setFrom(window.from)
    setTo(window.to)
    const at = new Date(Date.UTC(Number(window.to.slice(0, 4)), Number(window.to.slice(5, 7)) - 2, 1))
    setCursor({ year: at.getUTCFullYear(), month: at.getUTCMonth() })
  }, [open, query.range, window])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false) }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const step = (delta: number) => {
    const at = new Date(Date.UTC(cursor.year, cursor.month + delta, 1))
    setCursor({ year: at.getUTCFullYear(), month: at.getUTCMonth() })
  }

  /**
   * Primer clic fija el inicio; el segundo cierra el tramo y lo aplica. Un
   * tercero vuelve a empezar.
   *
   * El desplegable no se cierra solo: elegir fechas casi nunca sale a la
   * primera —te pasas un día, quieres probar la semana anterior— y tener que
   * reabrirlo cada vez convertía un ajuste en cuatro clics. Se cierra al pulsar
   * fuera o con Escape, como cualquier desplegable.
   */
  const pick = (day: string) => {
    if (!from || to) { setFrom(day); setTo(''); return }
    const [lo, hi] = day < from ? [day, from] : [from, day]
    setFrom(lo)
    setTo(hi)
    onChange({ ...query, range: 'custom', from: lo, to: hi })
  }

  const applyPreset = (value: AnalyticsRange) => {
    onChange({ range: value, compare: query.compare ?? 'previous' })
  }

  /* Un atajo propio es un tramo propio con nombre: se aplica como `custom` y
     mueve el calendario a donde empieza, para que se vea lo que se eligió. */
  const applyShortcut = (shortcut: RangeShortcut) => {
    setFrom(shortcut.from)
    setTo(shortcut.to)
    setCursor(cursorFor(shortcut.from, shortcut.to))
    onChange({ ...query, range: 'custom', from: shortcut.from, to: shortcut.to })
  }
  const activeShortcut = query.range === 'custom'
    ? shortcuts?.find((shortcut) => shortcut.from === query.from && shortcut.to === query.to)
    : undefined

  return (
    <div className="dash-range-picker" ref={boxRef}>
      <button
        type="button"
        className="dash-secondary-btn dash-range-trigger"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <IconCalendarEvent size={14} aria-hidden="true" />
        {/* Con un atajo propio puesto, su nombre: «Próximos 7 días» se lee
            mejor que dos fechas que hay que restar. */}
        <span>{activeShortcut ? t(activeShortcut.labelKey) : rangeLabel(i18n, query, window)}</span>
      </button>

      {open ? (
        <div className="dash-range-popover" role="dialog" aria-label={t('range.pickAria')}>
          <div className="dash-range-presets">
            {shortcuts ? (
              <div className="dash-range-preset-group">
                <span className="dash-range-preset-heading">{t('range.group.shortcuts')}</span>
                {shortcuts.map((shortcut) => {
                  const active = shortcut === activeShortcut
                  return (
                    <button
                      key={shortcut.labelKey}
                      type="button"
                      className={active ? 'is-active' : ''}
                      onClick={() => applyShortcut(shortcut)}
                    >
                      {t(shortcut.labelKey)}
                      {active ? <IconCheck size={13} /> : null}
                    </button>
                  )
                })}
              </div>
            ) : PRESET_GROUPS.map((group) => (
              <div className="dash-range-preset-group" key={group.id}>
                <span className="dash-range-preset-heading">{t(group.labelKey)}</span>
                {RANGE_PRESETS.filter((preset) => preset.group === group.id).map((preset) => (
                  <button
                    key={preset.value}
                    type="button"
                    className={query.range === preset.value ? 'is-active' : ''}
                    onClick={() => applyPreset(preset.value)}
                  >
                    {t(preset.labelKey)}
                    {query.range === preset.value ? <IconCheck size={13} /> : null}
                  </button>
                ))}
              </div>
            ))}
          </div>

          <div className="dash-range-calendar">
            <div className="dash-cal-head">
              <button type="button" aria-label={t('range.prevMonth')} onClick={() => step(-1)}><IconChevronLeft size={15} /></button>
              <span>{from ? (to ? `${prettyDay(from, language)} – ${prettyDay(to, language)}` : `${prettyDay(from, language)} – …`) : t('range.pickStart')}</span>
              <button type="button" aria-label={t('range.nextMonth')} onClick={() => step(1)}><IconChevronRight size={15} /></button>
            </div>
            <div className="dash-cal-months" onMouseLeave={() => setHover('')}>
              <MonthView {...cursor} from={from} to={to} hover={hover} max={max} onPick={pick} onHover={setHover} />
              <MonthView {...nextMonth(cursor)} from={from} to={to} hover={hover} max={max} onPick={pick} onHover={setHover} />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export { shiftDay, prettyDay, todayIn }
